import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Camera, Languages, Check, KeyRound, Ban, Trash2, MapPin, UserRound, ChevronRight } from "lucide-react";
import { SubPage, EmptyState, Spinner } from "../components/common";
import ListingCard from "../components/ListingCard";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ZoneDialog } from "../components/Dialogs";
import { Avatar } from "./MessagesPage";
import { api, apiError, mediaUrl } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { useAppData } from "../context/AppDataContext";
import { LANGUAGE_NAMES } from "../lib/i18n";

export function FavoritesPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { favorites } = useAppData();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/me/favorites").then((r) => setItems(r.data)); }, []);
  const visible = items?.filter((l) => favorites.has(l.id));
  return (
    <SubPage title={t("fav.title")} testid="favorites-page">
      {!items ? <div className="flex justify-center py-10"><Spinner /></div> : visible.length === 0 ? (
        <EmptyState icon={Heart} title={t("fav.empty")} sub={t("fav.emptySub")} testid="favorites-empty"
          action={<button className="btn-primary" onClick={() => navigate("/mercat")} data-testid="favorites-go-mercat-btn">{t("detail.goMercat")}</button>} />
      ) : (
        <div className="grid grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-3 lg:grid-cols-4" data-testid="favorites-grid">
          {visible.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
        </div>
      )}
    </SubPage>
  );
}

export function MyDataPage() {
  const { t } = useLang();
  const { user, updateMe } = useAuth();
  const [name, setName] = useState(user.name);
  const [birth, setBirth] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = async (patch) => {
    setBusy(true);
    try { await updateMe(patch); toast.success(t("data.saved")); } catch (e) { toast.error(apiError(e, t)); }
    setBusy(false);
  };
  const onAvatar = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error(t("err.fileType"));
    if (file.size > 10 * 1024 * 1024) return toast.error(t("err.fileSize"));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      await updateMe({ avatar_path: data.path });
      toast.success(t("data.saved"));
    } catch { toast.error(t("err.upload")); }
    setUploading(false);
  };

  return (
    <SubPage title={t("data.title")} testid="mydata-page">
      <div className="space-y-5 rounded-3xl bg-white p-6 shadow-[0_4px_20px_rgba(13,42,107,0.06)]">
        <div className="flex flex-col items-center">
          <button onClick={() => fileRef.current?.click()} className="group relative h-28 w-28 overflow-hidden rounded-full bg-brand/15" data-testid="mydata-avatar-btn">
            {user.avatar_url ? <img src={mediaUrl(user.avatar_url)} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />
              : <span className="text-4xl font-extrabold text-navy">{user.name?.[0]?.toUpperCase()}</span>}
            <span className="absolute inset-0 flex items-center justify-center bg-navy/40 text-white opacity-0 transition-opacity group-hover:opacity-100">
              {uploading ? <Spinner className="border-white/40 border-t-white" /> : <Camera className="h-6 w-6" />}
            </span>
          </button>
          <button onClick={() => fileRef.current?.click()} className="mt-2 text-sm font-bold text-brand" data-testid="mydata-change-avatar-btn">{t("data.changeAvatar")}</button>
          <input ref={fileRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => onAvatar(e.target.files[0])} data-testid="mydata-avatar-input" />
        </div>
        <label className="block">
          <span className="mb-1.5 block text-sm font-bold text-navy">{t("auth.name")}</span>
          <div className="flex gap-2">
            <input className="field flex-1" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} data-testid="mydata-name-input" />
            <button className="btn-primary px-5" disabled={busy || !name.trim() || name === user.name} onClick={() => save({ name })} data-testid="mydata-name-save-btn">{t("data.save")}</button>
          </div>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-bold text-navy">{t("auth.email")}</span>
          <input className="field bg-mist text-slate-500" value={user.email} disabled data-testid="mydata-email" />
        </label>
        <div>
          <span className="mb-1.5 block text-sm font-bold text-navy">{t("auth.birthDate")}</span>
          {user.birth_date ? (
            <>
              <input className="field bg-mist text-slate-500" type="date" value={user.birth_date} disabled data-testid="mydata-birthdate" />
              <p className="mt-1 text-xs text-slate-400">{t("data.birthLocked")}</p>
            </>
          ) : (
            <>
              <p className="mb-2 text-xs font-semibold text-alert">{t("data.birthMissing")}</p>
              <div className="flex gap-2">
                <input className="field flex-1" type="date" value={birth} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setBirth(e.target.value)} data-testid="mydata-birthdate-input" />
                <button className="btn-primary px-5" disabled={busy || !birth} onClick={() => save({ birth_date: birth })} data-testid="mydata-birthdate-save-btn">{t("data.save")}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </SubPage>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="rounded-3xl bg-white p-5 shadow-[0_4px_20px_rgba(13,42,107,0.06)]" data-testid={testid}>
      <p className="mb-3 flex items-center gap-2 font-bold text-navy"><Icon className="h-5 w-5 text-brand" /> {title}</p>
      {children}
    </div>
  );
}

function PasswordSection({ user }) {
  const { t } = useLang();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const google = user.auth_provider === "google";
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/users/me/password", { current_password: cur || null, new_password: pw });
      toast.success(t("set.pwSaved"));
      setCur(""); setPw("");
    } catch (err) {
      toast.error(err.response?.status === 400 ? t("set.pwWrong") : apiError(err, t));
    }
    setBusy(false);
  };
  return (
    <Section icon={KeyRound} title={t(google ? "set.createPassword" : "set.password")} testid="settings-password">
      <form onSubmit={save} className="space-y-2.5">
        {!google && <input className="field" type="password" required placeholder={t("set.currentPw")} value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" data-testid="settings-current-password" />}
        <input className="field" type="password" required minLength={6} placeholder={t("set.newPw")} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="settings-new-password" />
        <button className="btn-primary w-full py-3" disabled={busy || pw.length < 6} data-testid="settings-password-save">{t("data.save")}</button>
      </form>
    </Section>
  );
}

function BlockedSection() {
  const { t } = useLang();
  const [items, setItems] = useState(null);
  const load = () => api.get("/blocks").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const unblock = async (id) => { await api.delete(`/blocks/${id}`); toast.success(t("set.unblocked")); load(); };
  return (
    <Section icon={Ban} title={t("set.blocked")} testid="settings-blocked">
      {!items ? <Spinner /> : items.length === 0 ? <p className="text-sm text-slate-500" data-testid="settings-no-blocked">{t("set.noBlocked")}</p> : (
        <div className="space-y-2">
          {items.map((u) => (
            <div key={u.user_id} className="flex items-center gap-3" data-testid={`blocked-user-${u.user_id}`}>
              <Avatar user={u} size="h-10 w-10" />
              <span className="flex-1 truncate font-semibold text-navy">{u.name}</span>
              <button className="btn-outline px-4 py-1.5 text-sm" onClick={() => unblock(u.user_id)} data-testid={`unblock-btn-${u.user_id}`}>{t("set.unblock")}</button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function DeleteAccount() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState("");
  const expected = t("set.deleteWord");
  const remove = async () => {
    try {
      await api.delete("/users/me", { data: { confirm: true } });
      setUser(false);
      toast.success(t("set.deleted"));
      navigate("/", { replace: true });
    } catch (e) { toast.error(apiError(e, t)); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="w-full rounded-full border-[1.5px] border-alert py-3.5 font-bold text-alert transition-colors hover:bg-alert/5" data-testid="settings-delete-account-btn">
        <Trash2 className="mr-2 inline h-4 w-4" />{t("set.delete")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-sm rounded-3xl border-0 p-6" data-testid="delete-account-dialog">
          <DialogTitle className="text-xl font-extrabold text-navy">{t("set.deleteTitle")}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{t("set.deleteSub")}</DialogDescription>
          <label className="block text-sm font-semibold text-navy">{t("set.deleteType", { word: expected })}
            <input className="field mt-1.5" value={word} onChange={(e) => setWord(e.target.value)} data-testid="delete-account-confirm-input" />
          </label>
          <button className="w-full rounded-full bg-alert py-3 font-bold text-white disabled:opacity-40" disabled={word.trim().toUpperCase() !== expected} onClick={remove} data-testid="delete-account-confirm-btn">
            {t("set.deleteBtn")}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function SettingsPage() {
  const { t, lang, setLang, languages } = useLang();
  const { user, updateMe } = useAuth();
  const { zone, zoneName } = useAppData();
  const navigate = useNavigate();
  const [zoneOpen, setZoneOpen] = useState(false);
  const chooseLang = (k) => { setLang(k); if (user) updateMe({ language: k }).catch(() => {}); };
  const rowCls = "flex w-full items-center gap-4 rounded-2xl bg-white px-4 py-4 text-left shadow-[0_2px_12px_rgba(13,42,107,0.06)] transition-shadow hover:shadow-[0_6px_18px_rgba(13,42,107,0.1)]";
  return (
    <SubPage title={t("settings.title")} testid="settings-page">
      <div className="space-y-4">
        <Section icon={Languages} title={t("settings.language")} testid="settings-language">
          <div className="grid grid-cols-2 gap-2.5">
            {languages.map((k) => (
              <button key={k} onClick={() => chooseLang(k)} data-testid={`settings-lang-${k}`}
                className={`flex items-center justify-between rounded-2xl border-[1.5px] px-4 py-3 font-semibold transition-colors duration-200 ${lang === k ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-navy"}`}>
                {LANGUAGE_NAMES[k] || k} {lang === k && <Check className="h-4 w-4" />}
              </button>
            ))}
          </div>
        </Section>
        {user && (
          <>
            <button className={rowCls} onClick={() => setZoneOpen(true)} data-testid="settings-zone-btn">
              <MapPin className="h-5 w-5 text-alert" />
              <span className="flex-1 font-medium text-navy">{t("set.zone")}</span>
              <span className="font-semibold text-brand" data-testid="settings-zone-value">{zone === "all" ? t("zone.allRegion") : zoneName(zone)}</span>
            </button>
            <button className={rowCls} onClick={() => navigate("/mis-datos")} data-testid="settings-mydata-btn">
              <UserRound className="h-5 w-5 text-brand" />
              <span className="flex-1 font-medium text-navy">{t("set.myData")}</span>
              <ChevronRight className="h-5 w-5 text-brand" />
            </button>
            <PasswordSection user={user} />
            <BlockedSection />
            <DeleteAccount />
            <ZoneDialog open={zoneOpen} onOpenChange={setZoneOpen} />
          </>
        )}
      </div>
    </SubPage>
  );
}
